// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * DemoPreviewPage - Statische demo zonder login, Dexie of encryptie.
 * Toont een gevuld WilsApp-dossier van "Jan de Vries" als voorbeeld.
 * Publieke route: /demo-preview
 *
 * V41: aanpassingen
 *  - Tweede woning Italië (Marche, Rosora) + Anthony als lokaal contact
 *  - Beleggingen & optiehandel via DEGIRO
 *  - Uitgebreide checklist met internationale prioriteiten
 */
import { useNavigate } from 'react-router-dom'
import {
  User, Users, Heart, Home, FileText, Leaf,
  MessageSquare, ShieldCheck, CheckCircle2, ArrowRight,
  ClipboardList, BookOpen, Building2, PawPrint,
  ChevronDown, ChevronUp, AlertTriangle,
  Globe, TrendingUp, Phone, Smartphone,
} from 'lucide-react'
import { useState } from 'react'
import { ROUTES } from '@/lib/constants/routes'

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO = {
  naam:            'Jan de Vries',
  geboortedatum:   '15 april 1965',
  adres:           'Kerkstraat 12, 1234 AB Heiloo',
  email:           'jan.devries@voorbeeld.nl',
  telefoon:        '06 12 34 56 78',
  partner:         'Marie de Vries-Bakker',
  kinderen:        ['Sophie de Vries (1992)', 'Thomas de Vries (1995)'],
  huisdieren:      ['Max - golden retriever'],

  vp:        { naam: 'Peter Jansen',      relatie: 'Vriend',  status: 'Actief'  },
  reserveVp: { naam: 'Sophie de Vries',   relatie: 'Dochter', status: 'Tweede' },

  woning:    'Koopwoning - Kerkstraat 12, 1234 AB Heiloo',
  hypotheek: 'ABN AMRO - restschuld € 120.000',

  tweedeWoning: {
    land:     'Italië',
    regio:    'Marche',
    plaats:   'Rosora',
    type:     'Vakantiewoning',
    hypotheek:'Banca Intesa Sanpaolo - restschuld ca. € 28.000',
    contact: {
      naam:      'Anthony',
      rol:       'Beheerder / sleutelhouder',
      telefoon:  '+39 347 123 45 67',
      opmerking: 'Spreekt Engels',
    },
    documenten: {
      eigendomsakte: 'Bij notaio Marchetti, Rosora (Italië)',
      sleutels:      'Bij Anthony ter plaatse + reservesleutel in bureaulade thuis',
      hypotheek:     'Digitale map "Italië" - laptop, map Documenten',
    },
  },

  phoneInfo: {
    merk:         'Apple',
    model:        'iPhone 16 Pro',
    provider:     'KPN',
    authenticator:'Microsoft Authenticator',
    wachtwoord:   'Apple Wachtwoorden',
  },

  beleggingen: {
    broker:    'DEGIRO',
    type:      'Aandelen + optiehandel',
    rekening:  'Beleggingsrekening NR. DE****4521',
    opmerking: 'Actieve optieposities aanwezig - tijdig informeren bij overlijden om verlies te beperken',
    locatie:   'degiro.nl - inloggegevens in digitale kluis',
  },

  testament:       'Aanwezig bij notaris Van der Ploeg, Heiloo',
  levenstestament: 'Aanwezig - Marie is medisch vertegenwoordiger',
  donorregistratie:'Ja - alle organen',

  uitvaartwensen: [
    'Crematie gewenst',
    'Kleine besloten dienst, max. 40 personen',
    'Muziek: "What a Wonderful World" - Louis Armstrong',
    'Geen zwarte kleding - graag iets kleurrijks',
    'Bloemen: zonnebloemen',
  ],
  uitvaartverzekering: 'DELA - polisnummer 12345678, dekking € 8.500',

  berichten: [
    { ontvanger: 'Marie de Vries-Bakker', type: 'Partner', klaar: true  },
    { ontvanger: 'Sophie de Vries',       type: 'Dochter', klaar: true  },
    { ontvanger: 'Thomas de Vries',       type: 'Zoon',    klaar: false },
  ],

  documenten: [
    { naam: 'Paspoort',               locatie: 'Bovenste la bureau, slaapkamer' },
    { naam: 'Testament',              locatie: 'Bij notaris Van der Ploeg, Heiloo' },
    { naam: 'Hypotheekakte (NL)',      locatie: 'Rode map, archiefkast' },
    { naam: 'Eigendomsakte Italië',   locatie: 'Bij notaio Marchetti, Rosora' },
    { naam: 'Sleutels Italië',        locatie: 'Bij Anthony + reservesleutel bureaulade' },
    { naam: 'Italiaanse hypotheek',   locatie: 'Digitale map "Italië" - laptop' },
    { naam: 'DEGIRO beleggingen',     locatie: 'degiro.nl - inloggegevens in kluis' },
    { naam: 'Verzekeringspolis',      locatie: 'Digitale kluis - laptop' },
    { naam: 'Pensioenopgave',         locatie: 'Mijn Pensioenoverzicht.nl' },
  ],

  nabestaandenchecklist: [
    { taak: 'Overlijden melden bij gemeente',                           urgent: true  },

    { taak: 'Uitvaartverzorger contacteren',                           urgent: true  },
    { taak: 'Broker DEGIRO informeren over actieve optieposities', urgent: true  },
    { taak: 'Anthony bellen (+39 347 123 45 67) - beheerder Italië',    urgent: true  },
    { taak: 'Notaris Van der Ploeg bellen',                             urgent: true  },
    { taak: 'DELA uitvaartverzekering claimen',                         urgent: false },
    { taak: 'ABN AMRO - nalatenschapsrekening openen',                  urgent: false },
    { taak: 'Italiaanse hypotheekdocumenten controleren',               urgent: false },
    { taak: 'Beslissing Italiaanse woning: beheren of verkopen',        urgent: false },
    { taak: 'SVB - ANW-uitkering voor Marie aanvragen',                 urgent: false },
    { taak: 'Zorgverzekering opzeggen',                                 urgent: false },
    { taak: 'Digitale accounts afhandelen',                             urgent: false },
  ],
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function SectionCard({
  icon, title, accent = 'sage', children, defaultOpen = true,
}: {
  icon: React.ReactNode
  title: string
  accent?: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  const accentColors: Record<string, string> = {
    sage:   'bg-sage-50 text-sage-700 border-sage-200',
    amber:  'bg-amber-50 text-amber-700 border-amber-200',
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    teal:   'bg-teal-50 text-teal-700 border-teal-200',
    pink:   'bg-pink-50 text-pink-700 border-pink-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    rose:   'bg-rose-50 text-rose-700 border-rose-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  }
  const colors = accentColors[accent] ?? accentColors.sage

  return (
    <div className="rounded-2xl border border-sand-300 bg-white overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-sand-50 transition-colors"
      >
        <div className={`flex h-8 w-8 items-center justify-center rounded-xl border flex-shrink-0 ${colors}`}>
          {icon}
        </div>
        <p className="flex-1 text-sm font-semibold text-ink">{title}</p>
        {open
          ? <ChevronUp   className="h-4 w-4 text-ink-4 flex-shrink-0" />
          : <ChevronDown className="h-4 w-4 text-ink-4 flex-shrink-0" />
        }
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 flex flex-col gap-2 border-t border-sand-100">
          {children}
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-sand-100 last:border-0">
      <span className="text-xs text-ink-4 flex-shrink-0 w-36">{label}</span>
      <span className="text-xs text-ink font-medium text-right flex-1">{value}</span>
    </div>
  )
}

function Pill({ label, green }: { label: string; green?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
      green ? 'bg-sage-100 text-sage-700' : 'bg-sand-200 text-ink-3'
    }`}>
      {green && <CheckCircle2 className="h-3 w-3" />}
      {label}
    </span>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DemoPreviewPage() {
  const navigate = useNavigate()
  const tw  = DEMO.tweedeWoning
  const bl  = DEMO.beleggingen
  const tel = DEMO.phoneInfo

  return (
    <div className="min-h-screen bg-sand">

      {/* Header */}
      <header className="bg-moss px-5 pt-6 pb-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Leaf className="h-5 w-5 text-white/90" />
            <span className="text-sm font-semibold text-white">WilsApp</span>
            <span className="ml-auto text-xs bg-white/20 text-white/90 px-2.5 py-0.5 rounded-full font-medium">
              Voorbeeldgegevens
            </span>
          </div>
          <h1 className="text-xl font-semibold text-white leading-snug">
            Dossier Jan de Vries
          </h1>
          <p className="text-sm text-white/75 mt-0.5">
            Zo ziet WilsApp eruit als alles is ingevuld
          </p>
          <p className="text-xs text-white/60 mt-1.5 leading-relaxed">
            WilsApp helpt mensen al tijdens hun leven om hun wensen vast te leggen, zolang zij dat zelf kunnen bepalen.
          </p>
        </div>
      </header>

      {/* Demo banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-5 py-3">
        <div className="max-w-lg mx-auto flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Dit is een demonstratie.</strong> Alle gegevens zijn fictief en worden nergens opgeslagen.
            Begin uw eigen voorbereiding via "Zelf starten".
          </p>
        </div>
      </div>


      {/* Voortgang */}
      <div className="bg-white border-b border-sand-200 px-5 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-ink-3">Voortgang dossier</span>
            <span className="text-xs font-bold text-sage-700">11 van 11 modules ingevuld</span>
          </div>
          <div className="h-2 w-full rounded-full bg-sand-200">
            <div className="h-2 rounded-full bg-moss" style={{ width: '100%' }} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {['Persoonlijk', 'Familie', 'VP', 'Woning NL', 'Telefoon', 'Woning IT', 'Beleggingen', 'Uitvaart', 'Testament', 'Berichten', 'Documenten'].map(m => (
              <Pill key={m} label={m} green />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-5 flex flex-col gap-4">

        {/* 1. Persoonlijke gegevens */}
        <SectionCard icon={<User className="h-4 w-4" />} title="Persoonlijke gegevens" accent="amber">
          <Row label="Naam"          value={DEMO.naam} />
          <Row label="Geboortedatum" value={DEMO.geboortedatum} />
          <Row label="Adres"         value={DEMO.adres} />
          <Row label="E-mail"        value={DEMO.email} />
          <Row label="Telefoon"      value={DEMO.telefoon} />
        </SectionCard>

        {/* 2. Partner & familie */}
        <SectionCard icon={<Heart className="h-4 w-4" />} title="Partner & familie" accent="pink">
          <Row label="Partner" value={DEMO.partner} />
          <div className="py-1.5 border-b border-sand-100">
            <span className="text-xs text-ink-4 block mb-1">Kinderen</span>
            {DEMO.kinderen.map(k => (
              <p key={k} className="text-xs font-medium text-ink">{k}</p>
            ))}
          </div>
          <div className="py-1.5">
            <span className="text-xs text-ink-4 block mb-1">Huisdieren</span>
            <div className="flex items-center gap-1.5">
              <PawPrint className="h-3.5 w-3.5 text-amber-500" />
              <p className="text-xs font-medium text-ink">{DEMO.huisdieren[0]}</p>
            </div>
          </div>
        </SectionCard>

        {/* 3. Vertrouwenspersoon */}
        <SectionCard icon={<Users className="h-4 w-4" />} title="Vertrouwenspersoon" accent="teal">
          <div className="flex items-center justify-between rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-ink">{DEMO.vp.naam}</p>
              <p className="text-xs text-ink-4">{DEMO.vp.relatie} · Na overlijden</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Pill label="Eerste" green />
              <Pill label="Actief"  green />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-sand border border-sand-300 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-ink">{DEMO.reserveVp.naam}</p>
              <p className="text-xs text-ink-4">{DEMO.reserveVp.relatie} · Na overlijden</p>
            </div>
            <Pill label="Tweede" />
          </div>
          <p className="text-xs text-ink-4 pt-1">
            Sleutelcode is persoonlijk overhandigd. WilsApp slaat de code nooit in leesbare vorm op.
          </p>
        </SectionCard>

        {/* 4. Woning Nederland */}
        <SectionCard icon={<Home className="h-4 w-4" />} title="Woning - Nederland" accent="blue">
          <Row label="Type"      value={DEMO.woning} />
          <Row label="Hypotheek" value={DEMO.hypotheek} />
        </SectionCard>

        {/* 5. Telefoon & digitale toegang */}
        <SectionCard icon={<Smartphone className="h-4 w-4" />} title="Telefoon & digitale toegang" accent="blue">
          <Row label="Merk & model"   value={`${tel.merk} ${tel.model}`} />
          <Row label="Provider"       value={tel.provider} />
          <div className="py-1.5 border-b border-sand-100">
            <span className="text-xs text-ink-4 block mb-1.5">Authenticator-app</span>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-sage-500 flex-shrink-0" />
              <p className="text-xs font-medium text-ink">{tel.authenticator} - actief</p>
            </div>
          </div>
          <div className="py-1.5">
            <span className="text-xs text-ink-4 block mb-1.5">Wachtwoordmanager</span>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-sage-500 flex-shrink-0" />
              <p className="text-xs font-medium text-ink">{tel.wachtwoord} - actief</p>
            </div>
          </div>
          <div className="mt-1 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Zonder toegang tot de smartphone en authenticator-app is inloggen bij bank, DigiD en e-mail niet mogelijk.
            </p>
          </div>
        </SectionCard>

        {/* 7. Tweede woning Italië */}
        <SectionCard icon={<Globe className="h-4 w-4" />} title="Tweede woning - Italië" accent="teal">
          <Row label="Land"   value={tw.land} />
          <Row label="Regio"  value={tw.regio} />
          <Row label="Plaats" value={tw.plaats} />
          <Row label="Type"   value={tw.type} />
          <Row label="Hypotheek" value={tw.hypotheek} />

          {/* Lokaal contact */}
          <div className="mt-2 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3">
            <p className="text-xs font-semibold text-blue-700 mb-2">Lokaal aanspreekpunt</p>
            <div className="flex items-start gap-2.5">
              <Phone className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-ink">{tw.contact.naam}</p>
                <p className="text-xs text-ink-3">{tw.contact.rol}</p>
                <p className="text-xs text-ink-3">{tw.contact.telefoon}</p>
                <p className="text-xs text-sage-700 font-medium mt-0.5">✓ {tw.contact.opmerking}</p>
              </div>
            </div>
          </div>

          {/* Documentlocaties Italië */}
          <div className="mt-1">
            <p className="text-xs font-semibold text-ink-4 mb-1.5">Documentlocaties</p>
            {Object.entries(tw.documenten).map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-4 py-1.5 border-b border-sand-100 last:border-0">
                <span className="text-xs text-ink-4 flex-shrink-0 w-32 capitalize">
                  {k === 'eigendomsakte' ? 'Eigendomsakte' : k === 'sleutels' ? 'Sleutels' : 'Hypotheek IT'}
                </span>
                <span className="text-xs text-ink font-medium text-right flex-1">{v}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* 6. Beleggingen */}
        <SectionCard icon={<TrendingUp className="h-4 w-4" />} title="Beleggingen & optiehandel" accent="green">
          <Row label="Broker"    value={bl.broker} />
          <Row label="Type"      value={bl.type} />
          <Row label="Rekening"  value={bl.rekening} />
          <Row label="Locatie"   value={bl.locatie} />
          <div className="mt-1 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">{bl.opmerking}</p>
          </div>
        </SectionCard>

        {/* 7. Testament & levenstestament */}
        <SectionCard icon={<BookOpen className="h-4 w-4" />} title="Testament & levenstestament" accent="violet">
          <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-sage-700">Testament aanwezig</p>
              <p className="text-xs text-sage-600 mt-0.5">{DEMO.testament}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
            <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-sage-700">Levenstestament aanwezig</p>
              <p className="text-xs text-sage-600 mt-0.5">{DEMO.levenstestament}</p>
            </div>
          </div>
          <Row label="Donorregistratie" value={DEMO.donorregistratie} />
        </SectionCard>

        {/* 8. Uitvaartwensen */}
        <SectionCard icon={<Building2 className="h-4 w-4" />} title="Uitvaartwensen" accent="rose">
          <div className="flex flex-col gap-1.5 pt-1">
            {DEMO.uitvaartwensen.map((w, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-sage-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-ink">{w}</p>
              </div>
            ))}
          </div>
          <div className="mt-2 rounded-xl bg-sand border border-sand-300 px-4 py-2.5">
            <p className="text-xs font-semibold text-ink-3 mb-0.5">Uitvaartverzekering</p>
            <p className="text-xs text-ink">{DEMO.uitvaartverzekering}</p>
          </div>
        </SectionCard>

        {/* 9. Berichtenservice */}
        <SectionCard icon={<MessageSquare className="h-4 w-4" />} title="Berichtenservice" accent="sage">
          <p className="text-xs text-ink-4 pt-1">
            Persoonlijke berichten voor nabestaanden - verzonden door de vertrouwenspersoon na overlijden.
          </p>
          {DEMO.berichten.map((b, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-sand-200 px-4 py-2.5">
              <div>
                <p className="text-xs font-semibold text-ink">{b.ontvanger}</p>
                <p className="text-xs text-ink-4">{b.type}</p>
              </div>
              <Pill label={b.klaar ? 'Klaar' : 'Concept'} green={b.klaar} />
            </div>
          ))}
        </SectionCard>

        {/* 10. Documentwijzer */}
        <SectionCard icon={<ShieldCheck className="h-4 w-4" />} title="Documentwijzer" accent="blue">
          <p className="text-xs text-ink-4 pt-1">
            WilsApp slaat geen documenten op - alleen de locatie waar ze te vinden zijn.
          </p>
          {DEMO.documenten.map((d, i) => (
            <div key={i} className="flex items-start justify-between gap-4 py-1.5 border-b border-sand-100 last:border-0">
              <span className="text-xs font-semibold text-ink flex-shrink-0 w-36">{d.naam}</span>
              <span className="text-xs text-ink-3 text-right flex-1">{d.locatie}</span>
            </div>
          ))}
        </SectionCard>

        {/* 11. Nabestaandenchecklist */}
        <SectionCard icon={<ClipboardList className="h-4 w-4" />} title="Nabestaandenchecklist" accent="amber" defaultOpen={false}>
          <p className="text-xs text-ink-4 pt-1">
            Dit overzicht helpt nabestaanden in de eerste dagen na overlijden - inclusief de internationale situatie van Jan.
          </p>
          <div className="flex flex-col gap-1.5">
            {DEMO.nabestaandenchecklist.map((t, i) => (
              <div key={i} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 ${
                t.urgent ? 'bg-amber-50 border border-amber-200' : 'bg-sand border border-sand-200'
              }`}>
                {t.urgent
                  ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                  : <FileText      className="h-3.5 w-3.5 text-ink-4 flex-shrink-0" />
                }
                <p className={`text-xs flex-1 ${t.urgent ? 'font-semibold text-amber-800' : 'text-ink-3'}`}>
                  {t.taak}
                </p>
                {t.urgent && (
                  <span className="text-xs font-semibold text-amber-600 flex-shrink-0">Urgent</span>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* CTA */}
        <div className="rounded-2xl bg-gradient-to-br from-moss to-sage-700 p-6 text-white mt-2">
          <p className="text-base font-semibold mb-1">Dit is wat WilsApp voor u kan doen</p>
          <p className="text-sm text-white/80 leading-relaxed mb-5">
            Jan de Vries heeft zijn dossier compleet - inclusief zijn Italiaanse vakantiewoning en beleggingen.
            Zijn nabestaanden weten later waar ze de informatie kunnen vinden en bij welke instanties ze moeten aankloppen.
            Zelf ook beginnen? In 10 tot 15 minuten heeft u een eerste overzicht.
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ONBOARDING)}
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-white text-moss text-sm font-bold py-3.5 hover:bg-sand-50 active:scale-[0.98] transition-all"
            >
              Zelf starten
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="flex items-center justify-center gap-2 w-full rounded-xl border border-white/30 text-white/90 text-sm font-medium py-3 hover:bg-white/10 transition-colors"
            >
              Terug naar beginscherm
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-ink-4 pb-4">
          WilsApp is een hulpmiddel, geen officiële bron en geen juridisch document.<br />
          Het helpt u uw wensen en aanwijzingen overzichtelijk bij te houden voor uw nabestaanden.
        </p>

      </main>
    </div>
  )
}
